'use client';

import { useState, useRef } from 'react';
import { useRouter } from '@/i18n/routing';
import { updateSettings } from "@/lib/actions/settings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Save, Upload, X, Plus, Trash2 } from "lucide-react";
import { RichTextEditor } from "../shared/RichTextEditor";
import { MediaImg } from '@/components/shared/MediaImg';
import { parseSettingsJson } from '@/lib/utils';

interface AboutSettingsFormProps {
  initialData: Record<string, string>;
}

function parseStats(initialData: Record<string, string>) {
  const aboutStats = parseSettingsJson(initialData.about_stats, []);
  if (aboutStats.length > 0) return aboutStats;
  const homeStats = parseSettingsJson(initialData.home_stats, []);
  if (homeStats.length > 0) return homeStats;
  return [
    { label: 'Years of Experience', value: 15, suffix: '+' },
    { label: 'Clients Served', value: 100, suffix: '+' },
    { label: 'Programs Delivered', value: 500, suffix: '+' },
  ];
}

export function AboutSettingsForm({ initialData }: AboutSettingsFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingHero, setIsUploadingHero] = useState(false);
  const [isUploadingHomeAbout, setIsUploadingHomeAbout] = useState(false);
  const router = useRouter();
  const heroFileInputRef = useRef<HTMLInputElement>(null);
  const homeAboutFileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    about_hero_image: initialData.about_hero_image || '',
    home_about_image: initialData.home_about_image || '',
    home_about_headline: initialData.home_about_headline || '',
    home_about_description: initialData.home_about_description || '',
    about_headline: initialData.about_headline || '',
    about_description: initialData.about_description || '',
    about_vision: initialData.about_vision || '',
    about_mission: initialData.about_mission || '',
    about_why_simisi: initialData.about_why_simisi || '',
    about_overview: initialData.about_overview || '',
    about_values: parseSettingsJson(initialData.about_values, []),
    about_stats: parseStats(initialData),
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleManualChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleArrayChange = (field: 'about_values' | 'about_stats', index: number, subfield: string, value: string | number) => {
    setFormData((prev) => {
      const newArray = [...prev[field]];
      newArray[index] = { ...newArray[index], [subfield]: value };
      return { ...prev, [field]: newArray };
    });
  };

  const addArrayItem = (field: 'about_values' | 'about_stats', defaultValue: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], defaultValue]
    }));
  };

  const removeArrayItem = (field: 'about_values' | 'about_stats', index: number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_: any, i: number) => i !== index)
    }));
  };

  const uploadImage = async (
    file: File,
    field: 'about_hero_image' | 'home_about_image',
    setUploading: (v: boolean) => void
  ) => {
    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('type', 'settings');

    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: uploadData,
        credentials: 'include',
      });
      const result = await response.json();
      if (result.success) {
        setFormData(prev => ({ ...prev, [field]: result.url }));
        toast.success('Image uploaded');
      } else {
        toast.error('Upload failed');
      }
    } catch {
      toast.error('An error occurred');
    } finally {
      setUploading(false);
    }
  };

  const handleHeroFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadImage(file, 'about_hero_image', setIsUploadingHero);
  };

  const handleHomeAboutFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadImage(file, 'home_about_image', setIsUploadingHomeAbout);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const settingsToUpdate = {
      about_hero_image: formData.about_hero_image,
      home_about_image: formData.home_about_image,
      home_about_headline: formData.home_about_headline,
      home_about_description: formData.home_about_description,
      about_headline: formData.about_headline,
      about_description: formData.about_description,
      about_vision: formData.about_vision,
      about_mission: formData.about_mission,
      about_why_simisi: formData.about_why_simisi,
      about_overview: formData.about_overview,
      about_values: JSON.stringify(formData.about_values),
      about_stats: JSON.stringify(formData.about_stats),
    };

    try {
      const result = await updateSettings(settingsToUpdate);
      if (result.success) {
        toast.success('About Us settings updated successfully');
        router.refresh();
      } else {
        toast.error('Failed to update settings');
      }
    } catch {
      toast.error('An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8 max-w-5xl mx-auto pb-20">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-navy">About Us Settings</h1>
        <Button type="submit" className="bg-navy hover:bg-primary font-bold" disabled={isLoading}>
          {isLoading ? 'Saving...' : <><Save className="mr-2 h-4 w-4" /> Save Changes</>}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Homepage: About Us Section</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Homepage About Image</Label>
                {formData.home_about_image ? (
                  <div className="relative aspect-square max-w-xs rounded-lg border overflow-hidden group">
                    <MediaImg src={formData.home_about_image} alt="Home about" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, home_about_image: '' }))}
                      className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => homeAboutFileInputRef.current?.click()}
                    className="aspect-square max-w-xs rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50"
                  >
                    <Upload className="h-8 w-8 text-gray-400 mb-2" />
                    <span className="text-xs text-gray-400">
                      {isUploadingHomeAbout ? 'Uploading...' : 'Click to upload'}
                    </span>
                  </div>
                )}
                <input
                  type="file"
                  ref={homeAboutFileInputRef}
                  onChange={handleHomeAboutFileChange}
                  className="hidden"
                  accept="image/*"
                />
                <Input
                  value={formData.home_about_image}
                  onChange={handleChange}
                  name="home_about_image"
                  placeholder="Image URL"
                  className="text-xs"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="home_about_headline">Homepage Headline</Label>
                <RichTextEditor
                  value={formData.home_about_headline}
                  onChange={(val) => handleManualChange('home_about_headline', val)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="home_about_description">Homepage Description</Label>
                <RichTextEditor
                  value={formData.home_about_description}
                  onChange={(val) => handleManualChange('home_about_description', val)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>About Page Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="about_headline">Hero Headline</Label>
                <RichTextEditor
                  value={formData.about_headline}
                  onChange={(val) => handleManualChange('about_headline', val)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="about_description">Main Description</Label>
                <RichTextEditor
                  value={formData.about_description}
                  onChange={(val) => handleManualChange('about_description', val)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="about_vision">Vision</Label>
                <RichTextEditor
                  value={formData.about_vision}
                  onChange={(val) => handleManualChange('about_vision', val)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="about_mission">Mission</Label>
                <RichTextEditor
                  value={formData.about_mission}
                  onChange={(val) => handleManualChange('about_mission', val)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="about_why_simisi">Why Simisi Content</Label>
                <RichTextEditor
                  value={formData.about_why_simisi}
                  onChange={(val) => handleManualChange('about_why_simisi', val)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="about_overview">Company Overview</Label>
                <RichTextEditor
                  value={formData.about_overview}
                  onChange={(val) => handleManualChange('about_overview', val)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Company Values</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={() => addArrayItem('about_values', { title: '', description: '', icon: '' })}>
                <Plus className="h-4 w-4 mr-2" /> Add Value
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {formData.about_values.map((v: any, index: number) => (
                <div key={index} className="space-y-4 p-4 border rounded-lg relative group">
                  <Button type="button" variant="ghost" size="icon" className="absolute top-2 right-2 text-red-500" onClick={() => removeArrayItem('about_values', index)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Value Title</Label>
                      <Input value={v.title} onChange={(e) => handleArrayChange('about_values', index, 'title', e.target.value)} placeholder="e.g. Business-Driven" />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label>Lucide Icon Name</Label>
                        <a
                          href="https://lucide.dev/icons"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] text-primary hover:underline flex items-center gap-1"
                        >
                          (Find icons)
                        </a>
                      </div>
                      <Input value={v.icon || ''} onChange={(e) => handleArrayChange('about_values', index, 'icon', e.target.value)} placeholder="e.g. shield, lightbulb, rocket" />
                      <p className="text-[10px] text-gray italic">Use lowercase names from lucide.dev (e.g. &apos;shield-check&apos;)</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Value Description</Label>
                    <RichTextEditor value={v.description} onChange={(val) => handleArrayChange('about_values', index, 'description', val)} />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Hero Image</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {formData.about_hero_image ? (
                <div className="relative aspect-square rounded-lg border overflow-hidden group">
                  <MediaImg src={formData.about_hero_image} alt="Hero" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, about_hero_image: '' }))} className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div onClick={() => heroFileInputRef.current?.click()} className="aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50">
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                  <span className="text-xs text-gray-400">
                    {isUploadingHero ? 'Uploading...' : 'Click to upload'}
                  </span>
                </div>
              )}
              <input type="file" ref={heroFileInputRef} onChange={handleHeroFileChange} className="hidden" accept="image/*" />
              <Input value={formData.about_hero_image} onChange={handleChange} name="about_hero_image" placeholder="Image URL" className="text-xs" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Achievements (About Page)</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={() => addArrayItem('about_stats', { label: '', value: 0, suffix: '+' })}>
                <Plus className="h-4 w-4 mr-2" /> Add Stat
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Shown as circular orbit counters on the About Us page (max 6).
              </p>
              {formData.about_stats.map((s: any, index: number) => (
                <div key={index} className="space-y-3 p-3 border rounded-lg relative">
                  <Button type="button" variant="ghost" size="icon" className="absolute top-1 right-1 text-red-500 h-6 w-6" onClick={() => removeArrayItem('about_stats', index)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase">Value</Label>
                      <Input type="number" value={s.value} onChange={(e) => handleArrayChange('about_stats', index, 'value', parseInt(e.target.value))} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase">Suffix</Label>
                      <Input value={s.suffix} onChange={(e) => handleArrayChange('about_stats', index, 'suffix', e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase">Label</Label>
                    <Input value={s.label} onChange={(e) => handleArrayChange('about_stats', index, 'label', e.target.value)} />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

        </div>
      </div>
    </form>
  );
}
